import { formatLocalGameDate, todayLocalISO } from "@workspace/shared";

/**
 * Practice weeks (client 2026-11-04): Monday to Sunday, named by their days
 * ("Nov 2 - 8, 2026"), never typed. A game's week is the one its date falls
 * in: the day it was saved, or the day its coach moved it to in the Library.
 */

// "Sept", as every other date in the reports spells it.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A local day ("2026-11-04") at UTC midnight: day arithmetic with no DST. Null for no such day (Feb 30). */
const parse = (day: string): Date | null => {
  const m = DAY.exec(day);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return iso(d) === day ? d : null;
};
const iso = (d: Date) => d.toISOString().slice(0, 10);
const monthDay = (d: Date) => `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;

/** The Monday of a day's week: "2026-11-04" (a Wednesday) → "2026-11-02". "" for no day. */
export function weekStart(day: string): string {
  const d = parse(day);
  if (!d) return "";
  // Monday 0 … Sunday 6.
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return iso(d);
}

/**
 * The week a day falls in, by its days: "Nov 2 - 8, 2026", across months
 * "Sept 28 - Oct 4, 2026", across years "Dec 28, 2026 - Jan 3, 2027". "" for no day.
 */
export function weekLabel(day: string): string {
  const start = parse(weekStart(day));
  if (!start) return "";
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  const [startYear, endYear] = [start.getUTCFullYear(), end.getUTCFullYear()];
  if (startYear !== endYear) return `${monthDay(start)}, ${startYear} - ${monthDay(end)}, ${endYear}`;
  if (start.getUTCMonth() !== end.getUTCMonth()) return `${monthDay(start)} - ${monthDay(end)}, ${endYear}`;
  return `${monthDay(start)} - ${end.getUTCDate()}, ${endYear}`;
}

/**
 * A game's day in the Library, "2026-11-04": a saved game's date (the API's
 * instant, as the local day); the current game's is today — the day it
 * would be saved (client 2026-11-04). Its stored date is only when it was
 * started, just after the last save.
 */
export const libraryGameDay = (
  game: { complete?: boolean; game_date?: string | null },
  today: string = todayLocalISO(),
): string => (game.complete === false ? today : formatLocalGameDate(game.game_date));
